<template>
	<div ref="root" class="grow flex flex-col flex-wrap h-full">
		<slot
			name="toolbar"
			v-bind="{ selectedRows, openCreateTab }"
		>
			<AppDataGridToolbar
				v-if="!hideToolbar"
				class="mb-.5"
				:resource-key="resourceKey"
				:resource-name="gridName"
				:show-create-button="!hideCreateButton"
				:show-delete-button="!hideDeleteButton"
				:disable-delete-button="selectedRows.length < 1 || $isAllowed(`delete ${camelCaseResourceKey}`) && !$isAllowed(`deleteBulk ${camelCaseResourceKey}`) && selectedRows.length != 1"
				@create="openCreateTab"
				@delete="customDeleteFunction?.() ?? openDeleteConfirmation(selectedRows)"
			>
				<AppGridProfileButtons
					v-if="showProfileButtons"
					:grid-key="gridKey"
					:grid-api="gridApiRef"
				/>
				<slot
					name="toolbar-actions"
					v-bind="{ selectedRows, openCreateTab, gridInstanceId }"
				/>
			</AppDataGridToolbar>
		</slot>
		<AgGridVue
			class="ag-theme-alpine grow"
			row-model-type="serverSide"
			:grid-options="gridOptions"
			:getRowId="getRowId"
			@filter-changed="onFilterChanged"
			@sort-changed="persistColumnState"
			@column-visible="persistColumnState"
			@column-moved="persistColumnState"
			@column-resized="persistColumnState"
			@row-double-clicked="onDoubleClick($event)"
			@cell-selection-changed="onCellSelectionChanged"
			@selection-changed="
        defaultSelectionHandler();
        $emit('selected', $event);
      "
			@grid-ready="onGridReadyLocal"
			@column-everything-changed="onColumnsLoaded"
			@keydown="onKeyDown"
			v-bind="{
        paginationPageSize,
        cacheBlockSize,
        columnDefs,
        getContextMenuItems,
        paginationPageSizeSelector: [20, 50, 100, 200, 500, 1000],
        onCellValueChanged,
        ...({...$attrs, ['style']: undefined, ['class']: undefined}),
      }"
		/>
	</div>
</template>

<script setup lang="ts" generic="T extends Record<string, any>">
import {
	getDefaultGridPreset,
	getSingleGridColumnDefinition,
	globalAxios,
	QFood,
	removeEntities,
} from "@q-food/q-food";
import type {
	AgGridEvent,
	CellValueChangedEvent,
	ColDef,
	GridApi,
	GridOptions,
	GridReadyEvent,
	IRowNode,
	IServerSideGetRowsParams,
	MenuItemDef,
	NewColumnsLoadedEvent,
	RowDoubleClickedEvent,
} from "ag-grid-community";
import { AgGridVue } from "ag-grid-vue3";
import { fromPairs } from "lodash";
import AppDataGridStatusbar from "~/components/AppDataGridStatusbar.vue";
import LotRowSummaryTooltip from "~components/LotTracing/LotRowSummaryTooltip.vue";
import { translate } from "~/plugins/I18n.ts";
import { useAppTabs } from "~/stores/useAppTabs";
import type { Pageable } from "~/types";
import {
	convertFilterModelToRSQLString,
	convertRSQLStringToFilterModel,
	getNested,
	GROUP_NULL_KEY,
	groupKeysToRSQL,
} from "~/utils/Grids/GridRSQLBuilder";
import { consumePendingGridFilter } from "~/utils/Grids/AssistantGridFilter";
import { registerGrid, touchGrid, unregisterGrid, type RegisteredGrid } from "~/utils/Grids/AssistantScreenContext";
import { useApiResources } from "~stores/ApiResources";
import { useUserSettingsStore } from "~stores/UserSettings";
import { columnTypes } from "~utils/Grids/ColumnTypes";
import { AG_GRID_LOCALE_NL } from "~utils/Grids/GridLocalisation";
import { processFilterModel } from "~utils/Grids/GridProcessFilter.ts";
import { refreshGridsOfResource } from "~utils/Grids/RefreshGrid.ts";
import { processConfirmation } from "~utils/ConfirmationHelper.ts";
import AppDataGridBooleanCellRenderer from "./AppDataGridBooleanCellRenderer.vue";
import AppDataGridTotalsCellRenderer from "./AppDataGridTotalsCellRenderer.vue";
import AppDataGridFilterPresetsToolPanel from "./AppDataGridFilterPresetsToolPanel.vue";
import AppGridProfileButtons from "./AppGridProfileButtons.vue";
import { useUserStore } from "~stores/User.ts";
import { getFilterModel } from "~utils/Grids/FilterModelHelper.ts";

const { findByName } = useApiResources();
const { getGridColumnState, setGridColumnState } = useUserSettingsStore();
const { error, success, confirm } = useToast();

const props = withDefaults(
	defineProps<{
		resourceKey: string;
		resourceUrl: string;
		gridName: string;
		gridKey?: string; // Optional override if resource is used for multiple views which require distinct filters and preferences
		child?: boolean;
		pageSize?: number;
		hideToolbar?: boolean;
		hideCreateButton?: boolean;
		hideDeleteButton?: boolean;
		hideRefreshButton?: boolean;
		// Opt in (per grid, default off): show this grid's saved profiles as toggle buttons in the toolbar.
		showProfileButtons?: boolean;
		onDoubleClickGoToShowPage?: boolean;
		contextMenuItems?: (string | MenuItemDef)[];
		masterDetail?: boolean;
		preselectFirstRow?: boolean;
		// Opt in to the fast raw-SQL grid-data endpoint for flat/leaf rows instead of the per-resource index endpoint.
		useGridData?: boolean;
		filters?: string;
		// A stored RSQL criteria to pre-apply as real (visible, removable) column filters, reconstructed
		// against this grid's own column defs on grid-ready.
		rsqlFilters?: string;
		// Column ids to open the grid grouped by, outermost first. Only columns the server marked
		// groupable are honoured. Give such a grid its own `gridKey`: grouping is written back by
		// `persistColumnState`, so sharing a key would push it into the user's saved layout.
		rowGroupColumns?: string[];
		rowSelection?: any;
		// Field paths of columns to force-mark editable for this grid instance only (does not affect the
		// shared server-side grid definition). Persisting the edit is up to the consumer's cell-value-changed.
		editableColumns?: string[];
		isRowEditable?: (params: any) => boolean;
		calculateTotals?: (criteria: string) => Promise<Record<string, any>>;
		onRowDoubleClicked?: (e: RowDoubleClickedEvent) => void;
		onGridReady?: (e: GridReadyEvent) => void;
		customDeleteFunction?: Promise<void>;
		columns?: ColDef[];
		onPinnedCellValueChanged?: (event: CellValueChangedEvent) => void;
		onCellEdited?: (event: CellValueChangedEvent) => void;
		totalsIcons?: Record<string, string | { icon: string; tooltip?: string }>;
		// Opt in: lightweight hover preview per row, built from the lot row itself (no fetches).
		rowTooltip?: "lot";
		// Opt in: Excel-style fill handle (drag the cell's bottom-right corner to copy a value over a
		// range).
		fillHandle?: boolean;
	}>(),
	{
		child: false,
		pageSize: 100,
		rowSelection: "multiRow",
		preselectFirstRow: false,
		onDoubleClickGoToShowPage: false,
		useGridData: false,
		fillHandle: false,
		showProfileButtons: false,
	},
);

const gridKey = ref(props.gridKey ?? props.resourceKey);
const state = ref(undefined as string | undefined);
const { resourceKey, gridName, resourceUrl } = toRefs(props);

const gridApi = {
	_value: null as GridApi | null,
	get value() {
		if (this._value == null) {
			throw new Error("GridApi is not yet available");
		}

		return this._value;
	},
	// True once the grid has been torn down. A destroyed api does not throw — its calls just return
	// undefined — so anything resuming after an await (a datasource fetch, a totals call, a debounced
	// save) would otherwise fail further down on the result instead of here.
	get isGone() {
		return this._value == null || this._value.isDestroyed();
	},
};

const columnDefs = ref<ColDef[]>([]);
// Reactive mirror of the (non-reactive) gridApi handle, so template children like the profile buttons
// re-render once the api becomes available on grid-ready.
const gridApiRef = ref<GridApi | null>(null);
const paginationPageSize = ref(props.pageSize);
const cacheBlockSize = ref(100);
const selectedRows = ref<any[]>([]);
const refreshing = ref(false);
const criteriaUsedForTotals = ref<string | null>(null);
const previousResourceUrl = ref<string | null>(null);
const { isAllowed } = useUserStore();

const defaultSelectionHandler = () => {
	touchGrid(assistantGridContext);

	return (selectedRows.value = gridApi.value.getSelectedRows() ?? []);
};

// Excel-like selection: dragging a cell range across rows selects those rows. Ranges covering a single
// row are left alone — those come from a plain/ctrl/shift click, which AG Grid's own click selection
// (rowSelection.enableClickSelection) already handles. Syncing those here as well made both mechanisms
// fight each other: a ctrl+click got selected by one and immediately deselected again by the other.
function onCellSelectionChanged(event) {
	const api = event.api;
	const ranges = api.getCellRanges() ?? [];

	const selectedRowIndexes = new Set();
	for (const range of ranges) {
		if (!range.startRow || !range.endRow) continue;
		const start = range.startRow.rowIndex;
		const end = range.endRow.rowIndex;
		const [lo, hi] = start <= end ? [start, end] : [end, start];
		// Single-row range = a click, not a drag: leave the whole sync to AG Grid's click selection.
		if (lo === hi) return;
		for (let i = lo; i <= hi; i++) selectedRowIndexes.add(i);
	}

	if (selectedRowIndexes.size === 0) return;

	api.forEachNode((node) => {
		const shouldBeSelected = selectedRowIndexes.has(node.rowIndex);
		if (node.isSelected() !== shouldBeSelected) {
			node.setSelected(shouldBeSelected, false, "api");
		}
	});
}

const emit = defineEmits(["columns-loaded", "selected", "onDelete", "filtersChanged"]);

const gridInstanceId = Math.random().toString(36);

const gridOptions = Object.assign({}, {
	theme: "legacy",
	localeText: AG_GRID_LOCALE_NL,
	components: {
		AppDataGridBooleanCellRenderer,
		AppDataGridTotalsCellRenderer,
		AppDataGridStatusbar,
		AppDataGridFilterPresetsToolPanel,
		LotRowSummaryTooltip,
	},
	defaultColDef: {
		...(props.rowTooltip && {
			tooltipComponent: "LotRowSummaryTooltip",
			tooltipValueGetter: () => " ",
		}),
	},
	...(props.rowTooltip && {
		tooltipShowDelay: 400,
		tooltipInteraction: true,
	}),
	cellSelection: props.fillHandle ? { handle: { mode: "fill" } } : true,
	...(props.rowSelection != "none" && {
		rowSelection: {
			mode: props.rowSelection,
			checkboxes: true,
			enableClickSelection: true,
			headerCheckbox: false,
			ctrlASelectsRows: true,
		},
	}),
	pagination: true,
	masterDetail: props.masterDetail,
	columnTypes,
	// Server-side row grouping: the panel only appears once a whitelisted (enableRowGroup) column is
	// dragged in. Group value + aggregates come from the generic grid-data endpoint; leaf rows from
	// the normal index endpoint. See serverSideDatasource.getRows.
	rowGroupPanelShow: "onlyWhenGrouping",
	groupDisplayType: "singleColumn",
	autoGroupColumnDef: {
		minWidth: 260,
		cellRendererParams: {
			suppressCount: true,
			innerRenderer: groupInnerRenderer,
		},
	},
	statusBar: {
		statusPanels: [
			{
				key: "filterStatus",
				statusPanel: "AppDataGridStatusbar",
				align: "left",
				statusPanelParams: {
					onRefresh: () => {
						refresh(undefined, true);
					},
				},
			},
		],
	},
	sideBar: {
		toolPanels: [
			{
				id: "columns",
				labelDefault: "Columns",
				labelKey: "columns",
				iconKey: "columns",
				toolPanel: "agColumnsToolPanel",
				toolPanelParams: {
					suppressRowGroups: false,
					suppressValues: false,
					suppressPivots: true,
					suppressPivotMode: true,
				},
			},
			{
				id: "filters",
				labelDefault: "Filters",
				labelKey: "filters",
				iconKey: "filter",
				toolPanel: "agFiltersToolPanel",
			},
			{
				id: "filterPresets",
				labelDefault: "Profielen",
				labelKey: "filterPresets",
				iconKey: "menu",
				toolPanel: "AppDataGridFilterPresetsToolPanel",
				width: 400,
				toolPanelParams: {
					gridKey: gridKey.value,
				},
			},
		],
	},
} as GridOptions<T>);

const { execute } = useAsyncState(
	(params: URLSearchParams) => {
		const url = new URL(`${QFood.BASE_URL + resourceUrl.value}`);

		// If the url already contains criteria, we need to append the other search params
		let searchCriteria = url.searchParams.get("criteria");
		if (params.get("criteria") && searchCriteria) {
			searchCriteria = `${searchCriteria};${params.get("criteria")}`;
		} else if (params.get("criteria")) {
			searchCriteria = params.get("criteria");
		}

		// Append all params to the url
		params.forEach((value, key) => {
			url.searchParams.append(key, value);
		});
		url.searchParams.set("criteria", searchCriteria || "");

		return globalAxios
			.get(url.toString())
			.then((res) => res.data)
			.catch((err) => err);
	},
	undefined,
	{
		immediate: false,
		shallow: true,
	},
);
const resource = findByName(resourceKey.value);
const { push, currentRoute } = useRouter();

const root = useTemplateRef("root") as Ref<HTMLDivElement>;
// Kept at component scope so it can be disconnected again: an observer that outlives its grid still
// fires once when the element detaches (its size drops to 0) and then talks to a destroyed grid api.
let resizeObserver: ResizeObserver | undefined;

// Lets the assistant palette see which grid is on screen and what it is filtered to. `criteria()`
// mirrors exactly what loadRows sends: the base criteria baked into resourceUrl (child/split grids)
// plus the active column filters as RSQL. Note api.getFilterModel(), not getFilterModel(api) from
// FilterModelHelper — that wrapper rewrites today's date to the `$today` sentinel, which is not RSQL.
// Registered here rather than at grid-ready, which can fire again when the grid is re-created.
const assistantGridContext: RegisteredGrid = {
	resourceKey: () => resourceKey.value,
	element: () => root.value,
	criteria: () => {
		if (gridApi.isGone) return "";
		const api = gridApi.value;
		const baseCriteria = resourceUrl.value
			? new URL(`${QFood.BASE_URL + resourceUrl.value}`).searchParams.get("criteria")
			: null;
		const filterCriteria = convertFilterModelToRSQLString(api.getFilterModel(), api.getColumnDefs() ?? []);
		return [baseCriteria, filterCriteria].filter(Boolean).join(";");
	},
};
registerGrid(assistantGridContext);

onBeforeMount(async () => {
	if (!props.child && currentRoute.value.fullPath.includes("index")) {
	}

	try {
		state.value = await getDefaultGridPreset(gridKey.value);
	} catch (e) {
		// dan niet
	}
});

onActivated(() => {
	try {
		const firstSelectedNode = gridApi.value.getSelectedNodes()[0];
		if (firstSelectedNode) {
			gridApi.value.ensureNodeVisible(firstSelectedNode, "middle");
		}
	} catch {
		// do nothing
	}
});

watch([resourceKey, resourceUrl], (nv, ov) => {
	if (nv !== ov) {
		// AG Grid keeps the Server-Side selection (by row id) across the purge and re-selects the rows that
		// are still present, but it does NOT fire selection-changed for that re-application. Re-sync our
		// tracked selectedRows from the grid once the store has finished reloading (storeRefreshed fires after
		// the refresh completes) so slot consumers — button states, selectedRows binding — stay correct.
		const resyncSelection = () => {
			gridApi.value.removeEventListener("storeRefreshed", resyncSelection);
			selectedRows.value = gridApi.value.getSelectedRows() ?? [];
		};
		gridApi.value.addEventListener("storeRefreshed", resyncSelection);
		gridApi.value.refreshServerSide({ purge: true });
	}
});

// Inner renderer for the auto group column: show the grouped column's display value (dotted-field aware,
// enum-translated via refData) plus the server-provided child count.
function groupInnerRenderer(params: any) {
	// Only group rows carry a group value; leaf rows must render blank (their data is in the real columns).
	if (!params.node?.group) return "";
	const colDef = params.node?.rowGroupColumn?.getColDef?.();
	// For references, display the name (groupLabelPath) even though the key is the id (field).
	const labelPath = colDef?.context?.groupLabelPath || colDef?.field;
	let value = labelPath ? getNested(params.data, labelPath) : params.value;
	// The "(None)"/"Geen" group's key value is the GROUP_NULL_KEY sentinel (the server can't send a null key
	// or AG Grid can't round-trip it); render it as "none".
	if (value === GROUP_NULL_KEY) value = null;
	const refData = colDef?.refData;
	if (refData && value != null && refData[value] != null) value = refData[value];
	const label = value === null || value === undefined || value === "" ? translate("common.words.none") : value;
	const count = params.data?.__childCount;
	return count != null ? `${label} (${count})` : `${label}`;
}

const serverSideDatasource: GridOptions<Pageable>["serverSideDatasource"] = {
	async getRows(params: IServerSideGetRowsParams) {
		try {
			await loadRows(params);
		} finally {
			finishRefresh();
		}
	},
};

async function loadRows(params: IServerSideGetRowsParams): Promise<void> {
	if (resourceUrl.value == null) {
		console.warn("No resourceUrl provided, skipping grid data fetch");
		return params.fail();
	}

	const request = params.request;
	const rsqlQuery = convertFilterModelToRSQLString(request.filterModel, params.api.getColumnDefs());

	const pageSize = params.api.paginationGetPageSize();
	paginationPageSize.value = pageSize;
	cacheBlockSize.value = pageSize;

	// Any base criteria baked into resourceUrl (e.g. child/split grids) must survive grouping too.
	const baseCriteria = new URL(`${QFood.BASE_URL + resourceUrl.value}`).searchParams.get("criteria");

	const isGrouping = request.rowGroupCols.length > 0;
	const isGroupLevel = isGrouping && request.groupKeys.length < request.rowGroupCols.length;

	// --- Group-level rows: generic GROUP BY + aggregate endpoint ---
	if (isGroupLevel) {
		try {
			const body = {
				rowGroupCols: request.rowGroupCols,
				groupKeys: request.groupKeys,
				valueCols: request.valueCols,
				criteria: [baseCriteria, rsqlQuery].filter(Boolean).join(";"),
				sortModel: request.sortModel,
				startRow: request.startRow,
				endRow: request.endRow,
			};
			const json = await globalAxios
				.post(`${QFood.BASE_URL}/api/application/grid-data/${resourceKey.value}`, body)
				.then((res) => res.data);

			if (json && "content" in json) {
				params.success({ rowData: json.content, rowCount: json.page.totalElements });
			} else {
				params.fail();
			}
		} catch (e) {
			console.error(e);
			params.fail();
		}
		return;
	}

	// --- Leaf rows (or ungrouped): the normal per-resource index endpoint ---
	// Fold the expanded parent group keys into the RSQL criteria so we only fetch that group's rows.
	let criteria = rsqlQuery;
	if (isGrouping && request.groupKeys.length > 0) {
		const groupCriteria = groupKeysToRSQL(
			request.rowGroupCols,
			request.groupKeys,
			(params.api.getColumnDefs() ?? []) as ColDef[],
		);
		criteria = [criteria, groupCriteria].filter(Boolean).join(";");
	}

	const searchParams = new URLSearchParams({
		page: ((request.startRow ?? 0) / pageSize).toString(),
		size: pageSize.toString(),
		criteria,
	});

	request.sortModel.forEach((sort) => {
		searchParams.append("sort", `${sort.colId},${sort.sort}`);
	});

	const totalsCriteria = [baseCriteria, criteria].filter(Boolean).join(";");
	if (
		!isGrouping &&
		(totalsCriteria != criteriaUsedForTotals.value ||
			resourceUrl.value != previousResourceUrl.value ||
			refreshing.value)
	) {
		generatePinnedBottomData(totalsCriteria);
		criteriaUsedForTotals.value = totalsCriteria;
		previousResourceUrl.value = criteria;
	}

	// Opt-in: route flat/leaf rows through the fast raw-SQL grid-data endpoint (keyed by resourceKey),
	// otherwise use the per-resource index endpoint. Any base criteria baked into resourceUrl is preserved.
	const url = new URL(
		props.useGridData
			? `${QFood.BASE_URL}/api/application/grid-data/${resourceKey.value}`
			: `${QFood.BASE_URL + resourceUrl.value}`,
	);

	// If the url already contains criteria, we need to append the other search params
	let searchCriteria = url.searchParams.get("criteria") ?? (props.useGridData ? baseCriteria : null);
	if (searchParams.get("criteria") && searchCriteria) {
		searchCriteria = `${searchCriteria};${searchParams.get("criteria")}`;
	} else if (searchParams.get("criteria")) {
		searchCriteria = searchParams.get("criteria");
	}

	// Append all params to the url
	searchParams.forEach((value, key) => {
		url.searchParams.append(key, value);
	});
	url.searchParams.set("criteria", searchCriteria || "");

	const json = await globalAxios
		.get(url.toString())
		.then((res) => res.data)
		.catch((err) => err);

	if (json) {
		if (json == "Invalid permissions") {
			params.success({
				rowData: [],
				rowCount: 0,
			});
			throw Error(json);
		}

		if (!("content" in json)) {
			throw Error("Grid response should be paginated!");
		}

		const data = json?.content;

		params.success({
			rowData: data,
			rowCount: json.page.totalElements,
		});

		refreshing.value = false;

		// The grid can be gone by the time this request resolves; params.api is the one this fetch belongs
		// to, unlike gridApi.value which always hands back the most recently registered grid.
		const renderedNodes = params.api.isDestroyed() ? [] : (params.api.getRenderedNodes() ?? []);
		if (props.preselectFirstRow && renderedNodes.length > 0) {
			params.api.deselectAll();
			params.api.setNodesSelected({
				nodes: [renderedNodes[0]],
				newValue: true,
			});
		}
	} else {
		params.fail();
	}
}

function getContextMenuItems(): (string | MenuItemDef)[] {
	const actions = [];
	if (!props.hideCreateButton) {
		actions.push({
			name: "Nieuwe regel",
			action: openCreateTab,
		});
	}
	actions.push(
		...[
			{
				name: "Celwaarde kopiëren",
				action({ value, node, api, column }) {
					try {
						navigator.clipboard.writeText(value);
					} catch (e) {
						console.log(e);
						error("Kopiëren is mislukt");
					}
					api.flashCells({ rowNodes: [node!], columns: [column!] });
				},
			},
			{
				name: "Filter op celwaarde",
				action(params) {
					if (params.column?.isFilterAllowed()) {
						params.api.setFilterModel({
							[params.column.getColId()]: {
								type: "equals",
								filter: params.value,
							},
						});
					}
				},
			},
			...(props.contextMenuItems ?? []),
			"csvExport",
			"excelExport",
		],
	);
	return actions;
}

function openCreateTab() {
	const resource = findByName(resourceKey.value);
	if (resource) {
		push("/" + resource.name + "/create");
	}
}

const camelCaseResourceKey = resourceKey.value
	.split("-")
	.map((word, index) => {
		if (index == 0) {
			return word;
		}

		return word[0].toUpperCase() + word.slice(1);
	})
	.join("");

function openDeleteConfirmation(rows: Record<string, any>[]) {
	for (const row of rows) {
		if (!("id" in row)) return;
	}

	const single = props.rowSelection == "singleRow" || !isAllowed(`deleteBulk ${camelCaseResourceKey}`);

	// Both the per-item DELETE (ConfirmableData) and the bulk removeEntities (IdsToDeleteData) accept
	// `confirmedExceptions`. Keep it mutable so a 428 confirmation can append the confirmed classes and retry.
	const deleteData: { confirmedExceptions: string[] } = { confirmedExceptions: [] };

	const submitDelete = async () => {
		try {
			if (single) {
				await globalAxios.delete(`${resourceUrl.value}/${rows[0].id}`, { data: deleteData });
			} else {
				await removeEntities(resourceKey.value, {
					ids: rows.map((row) => row.id) as string[],
					confirmedExceptions: deleteData.confirmedExceptions,
				});
			}
			emit("onDelete");
			success(selectedRows.value.length <= 1 ? "Item is succesvol verwijderd!" : "Items zijn succesvol verwijderd!");
			refresh();
			refreshGridsOfResource(resourceKey.value);
		} catch (e: any) {
			// Backend asks for a yes/no confirmation (e.g. the lot already sits on a location). Add the
			// confirmed exception classes to the body and retry (a further 428 re-prompts).
			if (e?.status === 428 && e?.parameters?.errors) {
				processConfirmation(e.parameters.errors, deleteData, submitDelete);
				return;
			}
			console.log(e);
			error(e.message ?? "Er is iets misgegaan!");
			refresh();
			refreshGridsOfResource(resourceKey.value);
		}
	};

	// Resources whose backend delete always confirms drive a single prompt through the 428 loop above, so
	// skip the grid's own upfront confirm to avoid a double confirmation (backend adds a `deleteConfirmation`
	// action entry for those, see ResourceService).
	if ("deleteConfirmation" in (findByName(resourceKey.value)?.actions ?? {})) {
		submitDelete();
		return;
	}

	const translatedText =
		selectedRows.value.length <= 1
			? translate("common.messages.Are you sure you want to delete this item?")
			: translate("common.messages.Are you sure you want to delete the {0} selected items?", [
					selectedRows.value.length.toString(),
				]);

	confirm(translatedText, {
		denyButtonText: translate("common.words.no"),
		confirmButtonText: translate("common.words.yes"),
	}).then((result) => {
		if (!result.isConfirmed) return;
		submitDelete();
	});
}

function onDoubleClick(event: RowDoubleClickedEvent) {
	if (props.onRowDoubleClicked != undefined) {
		return props.onRowDoubleClicked(event);
	}

	const rowId = event.data.id;
	const resource = findByName(resourceKey.value);

	if (resource == undefined) return;
	if (!("update" in resource.actions)) return;

	if (!props.onDoubleClickGoToShowPage) {
		push(`/${resource.name}/update/${rowId}?resourceUrl=${resourceUrl?.value}`);
	} else {
		push(`/${resource.name}/show/?resourceId=${rowId}`);
	}
}

// Make the whole selection (checkbox) column cell clickable: a click anywhere in that cell toggles the
// row's selection additively — exactly like clicking the checkbox — instead of AG Grid's normal click,
// which replaces the selection (and would clear the other checked rows). We intercept in the capture
// phase so AG Grid's own selection handling never runs for these padding clicks; clicks on the checkbox
// itself are left untouched so AG handles them natively.
function onSelectionCellPointer(event: MouseEvent) {
	const target = event.target as HTMLElement | null;
	const cell = target?.closest('[col-id="ag-Grid-SelectionColumn"]');
	if (!cell) return;
	// The checkbox itself is handled natively by AG Grid.
	if (target!.closest(".ag-selection-checkbox")) return;

	event.stopPropagation();
	if (event.type !== "click") return; // only toggle on click; mousedown is suppressed to stop range-drag

	const rowIndex = cell.closest(".ag-row")?.getAttribute("row-index");
	if (rowIndex == null) return;

	const node = gridApi.value.getDisplayedRowAtIndex(Number(rowIndex));
	if (node?.selectable) node.setSelected(!node.isSelected());
}

async function onGridReadyLocal(params: GridReadyEvent) {
	gridApi._value = params.api;
	gridApiRef.value = params.api;
	params.api.setGridOption(
		"columnDefs",
		props.columns ?? (await loadColumnDefinition(resourceKey.value, props.rowSelection == "multiRow")),
	);
	await applyColumnState(gridKey.value, params.api);
	params.api.setGridOption("serverSideDatasource", serverSideDatasource!);

	if (state.value == undefined) {
		try {
			state.value = await getDefaultGridPreset(gridKey.value);
		} catch (e) {
			// dan niet
		}
	}

	// A second grid-ready (the grid being re-created) would otherwise leave the previous observer running.
	resizeObserver?.disconnect();
	resizeObserver = new ResizeObserver(() => {
		// The element detaching fires this one last time; by then the api is gone and its calls return
		// undefined instead of an array.
		if (params.api.isDestroyed()) return;
		if ((params.api.getEditingCells() ?? []).length > 0) return;

		params.api.resetRowHeights();
		params.api.redrawRows();
	});

	resizeObserver.observe(root.value);

	// Make the whole checkbox cell clickable (see onSelectionCellPointer).
	root.value.addEventListener("mousedown", onSelectionCellPointer, true);
	root.value.addEventListener("click", onSelectionCellPointer, true);

	if (props.onGridReady != undefined) {
		props.onGridReady(params);
	}

	if (state.value != undefined) {
		params.api.applyColumnState({
			state: JSON.parse(state.value.columnState),
			applyOrder: true,
		});

		params.api.setFilterModel(processFilterModel(JSON.parse(state.value.filterState)));
	}

	if (props.filters != undefined) {
		params.api.setFilterModel(JSON.parse(props.filters));
	}

	// A caller-supplied stored criteria (e.g. reopening a scheduled email's saved filter), applied the
	// same way as the assistant hand-off below so it shows as editable, removable column filters.
	if (props.rsqlFilters) {
		const filterModel = convertRSQLStringToFilterModel(
			props.rsqlFilters,
			(params.api.getColumnDefs() ?? []) as ColDef[],
		);
		if (filterModel) {
			params.api.setFilterModel(processFilterModel(filterModel));
		} else {
			useToast().warning("Het opgeslagen filter kon niet volledig als kolomfilter worden toegepast.");
		}
	}

	// Assistant "open grid" hand-off: apply the RSQL criteria for this resource as editable filter chips.
	const pendingCriteria = consumePendingGridFilter(resourceKey.value);
	if (pendingCriteria) {
		const filterModel = convertRSQLStringToFilterModel(pendingCriteria, (params.api.getColumnDefs() ?? []) as ColDef[]);
		if (filterModel) {
			params.api.setFilterModel(filterModel);
		} else {
			useToast().warning("Het filter kon niet volledig als kolomfilter worden toegepast.");
		}
	}

	// Open pre-grouped. Deliberately last: both `applyColumnState(gridKey, …)` above and the saved-preset
	// restore carry `rowGroup: false` for every column, so grouping applied any earlier is wiped. Only
	// columns the server marked groupable can be used — anything else is ignored by AG Grid and would
	// leave the grid silently flat, so it is reported instead.
	if (props.rowGroupColumns?.length) {
		const columnDefs = (params.api.getColumnDefs() ?? []) as ColDef[];
		const groupable = props.rowGroupColumns.filter((colId) =>
			columnDefs.some((c) => (c.colId === colId || c.field === colId) && c.enableRowGroup),
		);

		if (groupable.length) {
			params.api.applyColumnState({
				state: groupable.map((colId, index) => ({ colId, rowGroup: true, rowGroupIndex: index })),
			});
		}

		const missing = props.rowGroupColumns.filter((colId) => !groupable.includes(colId));
		if (missing.length) {
			console.warn(`[AppDataGrid] not groupable on '${resourceKey.value}': ${missing.join(", ")}`);
		}
	}
}

function onKeyDown(event: KeyboardEvent) {
	if (!["ArrowUp", "ArrowDown"].includes(event.key)) return;
	event.preventDefault();

	if (!gridApi.value.getFocusedCell()) return;
	const currentNodeIndex = gridApi.value.getFocusedCell().rowIndex;
	const nextNode = gridApi.value.getDisplayedRowAtIndex(currentNodeIndex);
	if (!nextNode) return;

	if (event.shiftKey && event.ctrlKey) {
		// TODO: go down or up and (de)select everything in between
	} else if (event.ctrlKey) {
		return;
	} else if (event.shiftKey) {
		if (nextNode.isSelected()) {
			const nodeIndexToDeselect = currentNodeIndex + (event.key === "ArrowUp" ? 1 : -1);
			gridApi.value.getDisplayedRowAtIndex(nodeIndexToDeselect).setSelected(false, false);
		} else {
			nextNode.setSelected(true, false);
		}
	} else {
		nextNode.setSelected(true, true);
	}
}

async function loadColumnDefinition(resourceKey: string, addCheckbox = true) {
	const columnDefinition = [] as ColDef[];

	// if (addCheckbox) {
	//   columnDefinition.push({ suppressColumnsToolPanel: true, suppressHeaderMenuButton: true, suppressHeaderContextMenu: true, headerCheckboxSelection:true, checkboxSelection: true, width: 40 });
	// }

	const newColumnDefs = await getSingleGridColumnDefinition(resourceKey);

	// @ts-ignore-next-line
	columnDefinition.push(
		...newColumnDefs.map((raw) => {
			const v: any = raw;
			if (v.cellEditor == "agRichSelectCellEditor") {
				if (v.cellEditorParams == undefined) v.cellEditorParams = {};
				v.valueFormatter = (params) =>
					params?.value != null && typeof params.value == "object" ? params.value.name : params?.value;
				v.cellEditorParams.formatValue = (option) =>
					option != null && typeof option == "object" ? option.name : option;
				const association =
					typeof v.field === "string" && v.field.endsWith(".name") ? v.field.slice(0, -".name".length) : null;
				if (association) {
					v.valueSetter = (params) => {
						params.data[association] = params.newValue ?? null;
						return true;
					};
				}
			}

			// Row grouping (backend-whitelisted). AG Grid keys a group by this column's `field` value, so for
			// references the field IS the id path (a hidden companion column); the name is shown via
			// groupLabelPath. Stash key/label/null paths for the group renderer + leaf-level drilldown filter.
			if (v.enableRowGroup) {
				v.context = {
					...(v.context ?? {}),
					groupKeyPath: v.groupKeyPath || v.field,
					groupLabelPath: v.groupLabelPath,
					groupNullPath: v.groupNullPath,
				};
				delete v.groupKeyPath;
				delete v.groupLabelPath;
				delete v.groupNullPath;
			}

			// Aggregatable value column: aggregated by default once grouping is active.
			if (v.aggFunc) {
				v.enableValue = true;
				v.allowedAggFuncs = [v.aggFunc];
			}

			// Per-instance editable override (does not touch the shared server-side grid definition).
			// When isRowEditable is provided, editability is gated per-row (e.g. by status).
			if (props.editableColumns?.includes(v.field)) {
				v.editable = props.isRowEditable ?? true;
			}

			// When the fill handle is enabled, only render it on editable cells — dragging it over a
			// read-only column can't change anything, so the handle there is just noise.
			if (props.fillHandle && !v.editable) {
				v.suppressFillHandle = true;
			}

			const totalsIcon = props.totalsIcons?.[v.field];
			if (totalsIcon) {
				const iconParams = typeof totalsIcon == "string" ? { icon: totalsIcon } : totalsIcon;
				v.cellRendererSelector = (params: any) =>
					params.node?.rowPinned == "bottom"
						? { component: "AppDataGridTotalsCellRenderer", params: iconParams }
						: undefined;
			}

			return v;
		}),
	);

	if (props.masterDetail) {
		columnDefinition[0]["cellRenderer"] = "agGroupCellRenderer";
	}

	console.log(columnDefinition);

	return columnDefinition;
}

async function applyColumnState(resourceKey: string, api: GridApi) {
	const columnState = await getGridColumnState(resourceKey);
	if (columnState) {
		api.applyColumnState({
			state: columnState,
			applyOrder: true,
		});
	} else if (!api.isDestroyed()) {
		await setGridColumnState(resourceKey, api.getColumnState());
	}
}

function onColumnsLoaded(e: NewColumnsLoadedEvent) {
	if (e.api.getColumnDefs()) {
		emit("columns-loaded", e);
	}
}

const controller = new AbortController();

async function generatePinnedBottomData(criteria: string) {
	const columnsToAggregate = [
		"availablePackageQuantity",
		"availableUnitQuantity",
		"availableWeight",
		"deliveredPackageQuantity",
		"deliveredUnitQuantity",
		"deliveredWeight",
		"packageQuantity",
		"unitQuantity",
		"weight",
		"totalValue",
		"totalPrice",
		"totalPriceBasedOnDelivery",
		"baseValue",
		"importCosts",
		"costs",
		"customsValue",
	] as const;

	let pinnedBottomRowData = {};

	if (props.calculateTotals == undefined) return;

	document.querySelector(".ag-root")?.classList?.add("grid-totals-loading");
	gridApi.value.setGridOption("pinnedBottomRowData", [Object.fromEntries(columnsToAggregate.map((k) => [k, 0]))]);

	pinnedBottomRowData = await props.calculateTotals(criteria);

	document.querySelector(".ag-root")?.classList?.remove("grid-totals-loading");

	if (!gridApi.isGone && Object.keys(pinnedBottomRowData).length > 0 && criteria == criteriaUsedForTotals.value) {
		gridApi.value.setGridOption("pinnedBottomRowData", [pinnedBottomRowData]);
	}
}

function onFilterChanged({ api }: AgGridEvent) {
	// Filtering is an unambiguous "I am working in this grid" signal — see AssistantScreenContext.
	touchGrid(assistantGridContext);

	const filterStatusBar = api.getStatusPanel<InstanceType<typeof AppDataGridStatusbar> | undefined>("filterStatus");

	if (filterStatusBar) {
		filterStatusBar.setFiltersActive(api.isAnyFilterPresent());
	}
	const filterModel = JSON.parse(getFilterModel(api));
	emit("filtersChanged", {
		filterModel: filterModel,
		criteria: convertFilterModelToRSQLString(filterModel, api.getColumnDefs()),
	});
}

const persistColumnState = useDebounceFn(() => {
	// Debounced, so it can land after the grid is gone — and then it would save an empty layout.
	if (gridApi.isGone) return;

	setGridColumnState(gridKey.value, gridApi.value.getColumnState());
}, 100);

/**
 * Given an update to a cell, this function will push the changes to the backend and refresh the grid.
 */
const onCellValueChanged = async (event: CellValueChangedEvent) => {
	if (event.node?.rowPinned) {
		props.onPinnedCellValueChanged?.(event);
		return;
	}

	if (props.onCellEdited) {
		props.onCellEdited(event);
		return;
	}

	const { column, data, api, value } = event;
	const resource = findByName(resourceKey.value);
	if (resource == undefined) return;

	const formDataEndpoints = ["purchase-order-row", "sales-order-row", "sales-order", "purchase-order"];

	// Make sure number values are processable by backend.
	let processedValue = value;
	if (column.getColDef().type == "NumberColumn" && typeof value === "string") {
		processedValue = value.replace(",", ".");
	}

	// Prepare payload for PUT endpoint. The form endpoint returns `{ fields, layout }`; only the
	// flat field list carries values.
	const originalData = (await globalAxios.get(`/api/application/forms/Edit${resource.camelCaseName}Form/${data.id}`))
		.data.fields;

	const submitValues = Object.assign({}, fromPairs(originalData.map((it) => [it.name, it.value])), {
		[column.getColId()]: processedValue,
	});

	// Retryable so a 428 confirmation can re-issue the PUT with `confirmedExceptions` added to `values`.
	const submit = async (values: Record<string, any>) => {
		let submitData: FormData | Record<string, any> = values;
		if (formDataEndpoints.includes(resourceKey.value)) {
			submitData = new FormData();
			submitData.append("data", new Blob([JSON.stringify(values)], { type: "application/json" }));
		}

		try {
			await globalAxios.put(`${resource.uri.toString()}/${data.id}`, submitData, {
				headers: {
					"Content-Type": undefined, // let Axios determine it
				},
			});
			success("Item is succesvol bijgewerkt!"); // Do not await!!
			refresh();
		} catch (e: any) {
			// 428 = backend needs a yes/no confirmation. Show one dialog, add the confirmed exception
			// classes to `values` and retry; do NOT refresh yet (that would revert before the answer).
			// On "No" (or dismiss) reload to undo the optimistic cell edit.
			if (e?.status === 428 && e?.parameters?.errors) {
				processConfirmation(e.parameters.errors, values, submit, undefined, () => refresh());
				return;
			}
			await error(e.message ?? "Er is iets misgegaan bij het updaten! Wijzigingen worden teruggedraaid.");
			refresh();
		}
	};

	await submit(submitValues);
};

let refreshInFlight = false;
let queuedRefresh: { dataIds: string[]; purge: boolean } | null = null;

function finishRefresh() {
	if (!refreshInFlight) return;
	refreshInFlight = false;
	if (queuedRefresh) {
		const next = queuedRefresh;
		queuedRefresh = null;
		refresh(next.dataIds, next.purge);
	}
}

function refresh(dataIds: string[] = [], purge = false) {
	if (gridApi._value == null) return;

	if (refreshInFlight) {
		queuedRefresh = {
			dataIds: [...new Set([...(queuedRefresh?.dataIds ?? []), ...dataIds])],
			purge: (queuedRefresh?.purge ?? false) || purge,
		};
		return;
	}

	const selectedNodeIds = gridApi.value.getSelectedNodes()?.map((node) => node.data?.id) ?? [];

	const selectRow = () => {
		gridApi.value.removeEventListener("storeRefreshed", selectRow);
		const nodes = [];
		const nodeIds = dataIds.length > 0 ? dataIds : selectedNodeIds;
		gridApi.value.forEachNode((node) => {
			if (!nodeIds.includes(node.data?.id)) return;
			nodes.push(node);
		});
		if (selectedNodeIds.length > 0) {
			gridApi.value.deselectAll();
		}
		gridApi.value.setNodesSelected({ nodes, newValue: true });
		if (nodes[0]) {
			gridApi.value.ensureNodeVisible(nodes[0], "middle");
		}
	};

	refreshInFlight = true;
	refreshing.value = true;
	gridApi.value.addEventListener("storeRefreshed", selectRow);
	gridApi.value.refreshServerSide({ purge: purge });
}

const onRefreshResource = (event: Event) => {
	if (!(event instanceof CustomEvent)) return;
	if (event.detail.resource !== resourceKey.value) return;

	if (event.detail.resourceId != undefined) {
		refresh([event.detail.resourceId]);
	} else {
		refresh();
	}
};

const onSelectAndRefreshGridRow = async (event: Event) => {
	if (!(event instanceof CustomEvent)) return;
	if (event.detail.resource !== resourceKey.value) return;
	if (event.detail.gridInstanceId !== gridInstanceId) return;
	gridApi.value.deselectAll();
	refreshGridsOfResource(resourceKey.value);
	const selectFirstGridRow = () => {
		selectGridRow(event);

		gridApi.value.removeEventListener("storeRefreshed", selectFirstGridRow);
	};
	gridApi.value.addEventListener("storeRefreshed", selectFirstGridRow);
};

const onSelectGridRow = async (event: Event) => {
	if (!(event instanceof CustomEvent)) return;
	if (event.detail.resource !== resourceKey.value) return;
	selectGridRow(event);
};

window.addEventListener("food:grid:refresh-resource", onRefreshResource);
window.addEventListener("food:grid:select-and-refresh-grid-row", onSelectAndRefreshGridRow);
window.addEventListener("food:grid:select-grid-row", onSelectGridRow);

onBeforeUnmount(() => {
	window.removeEventListener("food:grid:refresh-resource", onRefreshResource);
	window.removeEventListener("food:grid:select-and-refresh-grid-row", onSelectAndRefreshGridRow);
	window.removeEventListener("food:grid:select-grid-row", onSelectGridRow);
	root.value?.removeEventListener("mousedown", onSelectionCellPointer, true);
	root.value?.removeEventListener("click", onSelectionCellPointer, true);
	unregisterGrid(assistantGridContext);
	gridApiRef.value = null;
	resizeObserver?.disconnect();
	resizeObserver = undefined;
});

function selectGridRow(event: object) {
	let row = null;
	if (event.detail.rowId != null) {
		row = gridApi.value.getRowNode(event.detail.rowId);
		if (row == null) return;
	}

	gridApi.value.setNodesSelected({
		nodes: [row ?? gridApi.value.getRenderedNodes()[0]],
		newValue: true,
	});
}
</script>
<script lang="ts">
import {
	GridApi as localGridApi,
	IRowNode as localIRowNode,
} from "ag-grid-community";

export function updateRow(
	api: localGridApi,
	id: string,
	rowValue: string,
	rowKey: string,
) {
	const selectedRowIndex: number = api.getSelectedNodes()[0].rowIndex ?? 0;
	api?.forEachNode((rowNode: localIRowNode) => {
		if (rowNode.data.id == id) {
			const updated = rowNode.data;
			updated[rowKey] = rowValue;

			rowNode.updateData(updated);
		}
	});
	api.deselectAll();
	api.getDisplayedRowAtIndex(selectedRowIndex)?.setSelected(true);
}

function getRowId(params) {
	// Server-side group rows carry no entity id — key them by the path of parent group keys plus this
	// group's own key (provided by the backend as __groupRowId). Leaf rows keep the entity id.
	const data = params.data;
	if (data && data.__groupRowId !== undefined) {
		const parent = (params.parentKeys ?? []).join("/");
		return (parent ? `${parent}/` : "") + data.__groupRowId;
	}
	return data?.id ?? params.data?._tmpId;
}
</script>

<style>
/* The whole selection column cell toggles the row (see onSelectionCellPointer), so show a pointer. */
.ag-theme-alpine .ag-cell[col-id="ag-Grid-SelectionColumn"] {
	cursor: pointer;
}
</style>
